import type { Idea, AgentEvent } from '../types'
import type { AgentRunResult, HitlDecision } from './streamAgentTypes'
import { normalizeWireEvent } from './agUiNormalization'

const API_AGENT_STREAM = '/api/v1/agent/run/stream'
const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY_MS = 1000

export function consumeSseStringBuffer(
  chunkText: string,
  bufferStr: string,
  onPayload: (obj: unknown) => void,
): string {
  let buf = bufferStr + chunkText
  const parts = buf.split(/\r?\n\r?\n/)
  const tail = parts.pop() ?? ''
  for (const blk of parts) {
    const dataLines: string[] = []
    for (const line of blk.split(/\r?\n/)) {
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim())
      }
    }
    if (dataLines.length === 0) continue
    const dataPayload = dataLines.join('\n')
    try {
      onPayload(JSON.parse(dataPayload))
    } catch {
      /* SSE parcialmente escrito ou binário contaminado — ignora */
    }
  }
  return tail
}

/**
 * Execução F1–F4 via SSE até receber CUSTOM `archforge.run_output`.
 * Reconecta automaticamente com backoff exponencial em erros de rede
 * ou 429 (agente ocupado), respeitando o AbortSignal.
 */
export async function runAgentAgUiStream(opts: {
  roomId: string
  ideas: Pick<Idea, 'id' | 'title' | 'description' | 'category'>[]
  /** Decisões HITL da versão anterior — o Archie as considera na síntese. */
  hitlDecisions?: HitlDecision[]
  /** Fluxo do pipeline: 'brainstorm' (leve) ou 'docs' (pesado). Default 'full'. */
  mode?: 'brainstorm' | 'docs' | 'full' | 'resume'
  context?: Record<string, unknown>
  onEvents?: (events: AgentEvent[]) => void
  signal?: AbortSignal
}): Promise<AgentRunResult> {
  const base = typeof import.meta !== 'undefined' ? import.meta.env.VITE_API_URL || '' : ''
  const token = localStorage.getItem('token')

  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (opts.signal?.aborted) {
      throw new Error('Stream abortado pelo usuário')
    }

    if (attempt > 0) {
      const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1)
      await new Promise((resolve) => setTimeout(resolve, delay))
      if (opts.signal?.aborted) {
        throw new Error('Stream abortado pelo usuário')
      }
    }

    try {
      return await _runOnce({ ...opts, base, token })
    } catch (err: any) {
      lastError = err
      const status = err?.status
      // Não re-tenta em 401 (sessão expirada) nem em erros de parse/aplicação
      if (status === 401) throw err
      if (status === 429 && attempt < MAX_RETRIES) {
        opts.onEvents?.([]) // sinaliza reset para UI
        continue
      }
      // Erros de rede / timeout / 502 / 503 são retryáveis
      if (
        status === 502 ||
        status === 503 ||
        err?.message?.includes('timeout') ||
        err?.message?.includes('abortado')
      ) {
        if (attempt < MAX_RETRIES) continue
      }
      throw err
    }
  }

  throw lastError ?? new Error('Stream falhou após múltiplas tentativas')
}

async function _runOnce(opts: {
  roomId: string
  ideas: Pick<Idea, 'id' | 'title' | 'description' | 'category'>[]
  hitlDecisions?: HitlDecision[]
  mode?: 'brainstorm' | 'docs' | 'full' | 'resume'
  context?: Record<string, unknown>
  onEvents?: (events: AgentEvent[]) => void
  signal?: AbortSignal
  base: string
  token: string | null
}): Promise<AgentRunResult> {
  let buffer = ''
  const events: AgentEvent[] = []

  const res = await fetch(`${opts.base}${API_AGENT_STREAM}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      room_id: opts.roomId,
      ideas: opts.ideas,
      mode: opts.mode ?? 'full',
      ...(opts.context && Object.keys(opts.context).length ? { context: opts.context } : {}),
      ...(opts.hitlDecisions?.length ? { hitl_decisions: opts.hitlDecisions } : {}),
    }),
    signal: opts.signal,
  })

  if (res.status === 401) {
    throw Object.assign(new Error('Sessão expirada'), { status: 401 })
  }
  if (res.status === 429) {
    throw Object.assign(new Error('Agente ocupado (429).'), { status: 429 })
  }
  if (!res.ok) {
    throw Object.assign(new Error(`Fluxo SSE falhou: HTTP ${res.status}`), {
      status: res.status,
    })
  }

  const reader = res.body?.getReader()
  if (!reader) throw new Error('Fluxo SSE sem body')

  let completion: AgentRunResult | null = null
  let upstreamErrorMessage: string | null = null

  const handlePayload = (raw: unknown) => {
    const normalized = normalizeWireEvent(raw)
    if (!normalized) return
    if (normalized.type === 'RUN_ERROR') {
      upstreamErrorMessage = String(normalized.message || 'RUN_ERROR')
      return
    }
    if (
      normalized.type === 'CUSTOM' &&
      normalized.name === 'archforge.run_output'
    ) {
      completion = agentRunFromCustomValue(normalized.value)
      return
    }
    events.push(normalized as AgentEvent)
    opts.onEvents?.(events.slice())
  }

  try {
    const dec = new TextDecoder()
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (opts.signal?.aborted) {
        throw new Error('Stream abortado pelo usuário')
      }
      const { value, done } = await reader.read()
      if (done) break
      if (value?.length) {
        buffer = consumeSseStringBuffer(dec.decode(value, { stream: true }), buffer, handlePayload)
      }
    }
    if (buffer.trim()) {
      buffer = consumeSseStringBuffer('\n\n', buffer, handlePayload)
    }
  } finally {
    reader.releaseLock()
  }

  if (upstreamErrorMessage) {
    throw Object.assign(new Error(upstreamErrorMessage), { status: 502 })
  }

  if (!completion) {
    throw new Error('Stream encerrado sem pacote CUSTOM archforge.run_output')
  }

  const runResult: AgentRunResult = completion
  if (runResult.events.length > 0) {
    return runResult
  }
  return { ...runResult, events }
}

function agentRunFromCustomValue(value: unknown): AgentRunResult {
  const v = (value ?? {}) as Record<string, unknown>
  // Reconcilia o flag de fallback do protótipo: além do `is_fallback` embutido no
  // dict (injetado pelo fallback), respeita o `prototype_is_fallback` de nível
  // superior — assim o aviso não some se o embutido faltar.
  let prototype = (v.prototype as Record<string, unknown> | null) ?? null
  if (prototype && v.prototype_is_fallback && !prototype.is_fallback) {
    prototype = { ...prototype, is_fallback: true }
  }
  return {
    diagrams: normalizeDiagrams(v.diagrams),
    architecture: String(v.architecture || ''),
    prototype,
    events: normalizeAgentEventsInValue(v.events),
    event_storm: (v.event_storm as unknown[]) || [],
    event_storm_is_fallback: Boolean(v.event_storm_is_fallback),
    chat_messages: Array.isArray(v.chat_messages) ? (v.chat_messages as AgentRunResult['chat_messages']) : [],
    recommended_ideas: Array.isArray(v.recommended_ideas) ? (v.recommended_ideas as AgentRunResult['recommended_ideas']) : [],
  }
}

function normalizeDiagrams(raw: unknown): AgentRunResult['diagrams'] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((d): d is Record<string, unknown> => !!d && typeof d === 'object')
    .map((d) => ({
      name: typeof d.name === 'string' ? d.name : '',
      code: typeof d.code === 'string' ? d.code : '',
      type: typeof d.type === 'string' ? d.type : null,
    }))
    .filter((d) => d.code.trim().length > 0)
}

function normalizeAgentEventsInValue(events: unknown): AgentEvent[] {
  if (!Array.isArray(events)) return []
  return events.map((e) => normalizeWireEvent(e)).filter(Boolean) as AgentEvent[]
}
